import type { Product } from '@/types/chat';

/**
 * Canonical short product-type labels, matched against a product's own
 * `title` + `families` — the packaging string on a variant (e.g.
 * "1 Petit Sachet plante 90g") can't distinguish a herbal tea from a foot
 * bath, since both ship in the same sachet. Only the product's own title
 * and family taxonomy actually say what it is.
 *
 * Order matters: "Bain de pieds" is checked before anything else because a
 * foot bath's title/families can otherwise look identical to a herbal tea
 * once packaging is out of the picture (both may mention "plante").
 */
const PRODUCT_TYPE_PATTERNS: Array<{ label: string; test: RegExp }> = [
  { label: 'Bain', test: /bains? de pieds/ },
  { label: 'Gélules', test: /gelules?|capsules?/ },
  { label: 'Tisane', test: /tisanes?/ },
  { label: 'Huile', test: /huiles?|\boil\b/ },
  { label: 'Crème', test: /cremes?/ },
  { label: 'Kit', test: /\bkits?\b/ },
];

/**
 * Strip accents and lowercase. The catalog's `families` values are
 * unaccented ("Sante", not "Santé"), but `title` may not be — normalizing
 * both sides means the same keyword list works regardless.
 */
function normalize(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * A family entry is never a product type when it's a promotional tag —
 * e.g. "Jusqua 30percent" (the slug-mangled "Jusqu'à 30%"). Filtered out
 * before matching so a promo tag can never masquerade as a type.
 */
function isPromotionalFamily(family: string): boolean {
  return /\d/.test(family) || /percent/i.test(family);
}

/**
 * Detects a product's short type label ("Tisane", "Bain", "Gélules", ...)
 * from its own `title` and `families` — the only fields that actually
 * carry the product type. Returns null when nothing matches, so callers
 * can fall back to weaker signals (variant packaging strings) themselves.
 *
 * Shared between ProductCard's format badge and ProductPreviewPane's
 * tagline so the two surfaces always agree on what a product is.
 */
export function detectProductType(product: Pick<Product, 'title' | 'families'>): string | null {
  const families = (product.families ?? []).filter((family) => !isPromotionalFamily(family));
  const haystack = normalize([product.title, ...families].join(' '));

  for (const { label, test } of PRODUCT_TYPE_PATTERNS) {
    if (test.test(haystack)) return label;
  }
  return null;
}
